import ChevronRightIcon from '@kijiji/icons/src/icons/ChevronRight'
import ExternalLinkIcon from '@kijiji/icons/src/icons/ExternalLink'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { BadgeWithListingImage } from '@/components/vip/vip-autocan-features/BadgeWithListingImage'
import { BadgeWithLocalImage } from '@/components/vip/vip-autocan-features/BadgeWithLocalImage'
import {
  AutocanBadgeContainer,
  AutocanBadgeContent,
  AutocanBadgeIllustrationContainer,
  AutocanBadgeImage,
  AutocanBadgeSubtitle,
  AutocanBadgeTitle,
  AutocanBadgeWrapper,
  AutocanExternalLink,
} from '@/components/vip/vip-autocan-features/styled'
import { type AutocanFeatureConfig } from '@/components/vip/vip-autocan-features/utils'
import { Flex } from '@/ui/atoms/flex'

export type AutocanBadgeProps = AutocanFeatureConfig & {
  externalLink?: string
  handleFeatureClick?: () => void
  /**
   * @param isMobileVersion - Defines if we should remove the internal button/link from the DOM since its wrapper is already a button/link
   * */
  isMobileVersion?: boolean
  useInlineVariant: boolean
}

export const AutocanBadge: FC<AutocanBadgeProps> = ({
  bgColor,
  bgColorLight,
  externalLink,
  handleFeatureClick,
  icon,
  inlineBadgeImage,
  isMobileVersion,
  labels,
  listingImage,
  mobileBadgeImage,
  mobileIcon,
  singleBadgeImage,
  useInlineVariant,
}) => {
  const { colors, spacing } = useTheme()
  const { link, shortSubtitle, shortTitle, subtitle, title } = labels

  return (
    <AutocanBadgeWrapper useInlineVariant={useInlineVariant}>
      <AutocanBadgeContainer
        bgColor={bgColor}
        bgColorLight={bgColorLight}
        flexDirection="row"
        gap={spacing.default}
        useInlineVariant={useInlineVariant}
      >
        <AutocanBadgeIllustrationContainer
          useInlineVariant={useInlineVariant}
          data-testid="autocan-badge-illustration"
        >
          <AutocanBadgeImage
            bgColor={bgColor}
            hasListingImage={!!listingImage}
            useInlineVariant={useInlineVariant}
          >
            {listingImage ? (
              <BadgeWithListingImage
                icon={icon}
                listingImage={listingImage}
                useInlineVariant={useInlineVariant}
              />
            ) : (
              <BadgeWithLocalImage
                bgColor={bgColor}
                inlineBadgeImage={inlineBadgeImage}
                mobileBadgeImage={mobileBadgeImage}
                mobileIcon={mobileIcon}
                singleBadgeImage={singleBadgeImage}
                useInlineVariant={useInlineVariant}
              />
            )}
          </AutocanBadgeImage>
        </AutocanBadgeIllustrationContainer>

        <AutocanBadgeContent useInlineVariant={useInlineVariant}>
          <Flex flexDirection="column" gap={spacing.mini}>
            <AutocanBadgeTitle
              color={colors.grey.primary}
              data-testid="autocan-badge-title"
              hasShortTitle={!!shortTitle}
              size="medium"
              textAlign="start"
              weight="bold"
            >
              <span>{title}</span>
              {/* Will only appear on MOBILE */}
              {shortTitle ? <span>{shortTitle}</span> : null}
            </AutocanBadgeTitle>

            <AutocanBadgeSubtitle
              color={colors.grey.light1}
              data-testid="autocan-badge-subtitle"
              size="medium"
              textAlign="start"
            >
              <span>{subtitle}</span>
              <span>{shortSubtitle}</span>
            </AutocanBadgeSubtitle>
          </Flex>

          {!isMobileVersion && (externalLink || handleFeatureClick) ? (
            <AutocanExternalLink
              forwardedAs={externalLink ? 'a' : 'button'}
              href={externalLink ?? undefined}
              onClick={handleFeatureClick}
              size="small"
              target="_blank"
              useInlineVariant={useInlineVariant}
              variant="secondary"
            >
              {link}
              <ExternalLinkIcon aria-hidden />
            </AutocanExternalLink>
          ) : null}

          <ChevronRightIcon aria-hidden />
        </AutocanBadgeContent>
      </AutocanBadgeContainer>
    </AutocanBadgeWrapper>
  )
}
